"use client";

import { Link, useLocation } from "react-router";
import { useState, useEffect } from "react";
import { BarChart3, Menu, Settings, X } from "lucide-react";
import SettingsDropdown from "../assistant/SettingsDropdown";
import { useAuth } from "@workos-inc/authkit-react";
import api from "../../http";
import ProfileImageModal from "../assistant/ProfileImageModal";
import { appConfig } from "@/appConfig";
import { useColor } from "@/contexts/ColorContext";

interface AdminSidebarProps {
  collapsed?: boolean;
}

export default function AdminSidebar({ collapsed = false }: AdminSidebarProps) {
  const location = useLocation();
  const { getAccessToken, role } = useAuth();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<{
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  } | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const { themeColor, logoName } = useColor();
  const APP_NAME = appConfig.features.isDemoMode ? logoName : appConfig.name;
  const APP_COLOR = appConfig.features.isDemoMode
    ? themeColor
    : appConfig.themeColor;
  
  // Demo mode: show text, production mode: show logo from config
  const LOGO_SRC = appConfig.logo;

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Load current user information
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const token = await getAccessToken();
        const userData = await api.getCurrentUser(token);
        setCurrentUser(userData);
      } catch (error) {
        console.error("Failed to load current user:", error);
      }
    };

    loadCurrentUser();
  }, [getAccessToken]);

  const handleProfileImageUpdate = (imageUrl: string) => {
    if (currentUser) {
      setCurrentUser({
        ...currentUser,
        profile_image_url: imageUrl,
      });
    }
  };

  const handleUserInfoUpdate = (updatedUser: {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email: string;
    profile_image_url?: string | null;
  }) => {
    setCurrentUser(updatedUser);
  };

  // Determine active page based on current route
  const getActivePage = () => {
    const path = location.pathname;
    if (path === "/analytics") return "analytics";
    if (path === "/users") return "users";
    if (path === "/documents") return "documents";
    if (path === "/answers") return "answers";
    if (path === "/settings") return "settings";

    return "";
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/analytics") return "Analytics";
    if (path === "/users") return "Benutzer";
    if (path === "/documents") return "Dokumente";
    if (path === "/answers") return "Generierte Antworten";
    if (path === "/settings") return "Einstellungen";
    return `${APP_NAME} ${role === "superadmin" ? "Superadmin" : "Admin"}`;
  };

  const activePage = getActivePage();

  // Navigation items for cleaner code
  const navItems = [
    {
      to: "/answers",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ color: activePage === "answers" ? APP_COLOR : "#6B7280" }}
        >
          <path
            d="M20 6L12 2L4 6M20 6L12 10M20 6V18L12 22M12 10L4 6M12 10V22M4 6V18L12 22"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      label: "Generierte Antworten",
      active: activePage === "answers",
    },
    {
      to: "/documents",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ color: activePage === "documents" ? APP_COLOR : "#6B7280" }}
        >
          <path
            d="M8 7H16M8 12H16M8 17H12M4 4H20V20H4V4Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      label: "Dokumente",
      active: activePage === "documents",
    },
    {
      to: "/users",
      icon: (
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ color: activePage === "users" ? APP_COLOR : "#6B7280" }}
        >
          <path
            d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 14C8.13401 14 5 17.134 5 21H19C19 17.134 15.866 14 12 14Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      label: "Benutzer verwalten",
      active: activePage === "users",
    },
    {
      to: "/settings",
      icon: <Settings size={20} />,
      label: "Einstellungen",
      active: activePage === "settings",
    },
    // Analytics is only visible to superadmins
    ...(appConfig.features.analyticsDashboard && role === "superadmin"
      ? [
          {
            to: "/analytics",
            icon: (
              <BarChart3
                size={20}
                color={activePage === "analytics" ? APP_COLOR : "#6B7280"}
              />
            ),
            label: "Analytics",
            active: activePage === "analytics",
          } as const,
        ]
      : []),
  ];

  return (
    <>
      {/* Mobile Header - only show when sidebar is closed */}
      {!isMobileOpen && (
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-4 py-3">
          <div className="relative flex items-center justify-between h-full">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsMobileOpen(!isMobileOpen)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <Menu size={20} />
              </button>
            </div>

            {/* Centered Title (horizontally and vertically) */}
            <h2 className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-lg font-bold text-slate-800 text-center">
              {getPageTitle()}
            </h2>

            <div className="flex items-center gap-2">
              <SettingsDropdown
                onOpenProfileModal={() => setIsProfileModalOpen(true)}
                currentUser={currentUser}
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:static inset-y-0 left-0 z-40
          transform transition-transform duration-300 ease-in-out
          lg:transform-none
          ${isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          w-64 lg:w-auto
          bg-white border-r border-gray-200 shadow-lg lg:shadow-none
          rounded-3xl rounded-3xl lg:rounded-none
        `}
      >
        <div className="flex flex-col h-full">
          {/* Logo + Icons - hidden on mobile */}
          <div className="hidden lg:flex p-4 items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              {!collapsed && (
                <>
                  {appConfig.features.isDemoMode ? (
                    <span className="font-bold text-3xl" style={{ color: APP_COLOR }}>
                      {APP_NAME}
                    </span>
                  ) : (
                    <img
                      src={LOGO_SRC}
                      alt="Logo"
                      className="h-12 w-auto"
                    />
                  )}
                  <span className="bg-slate-100 text-xs px-2 py-1 rounded-md font-medium">
                    {role === "superadmin" ? "Superadmin" : "Admin"}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Mobile Header in Sidebar - only show when sidebar is open */}
          <div className="lg:hidden p-4 flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                {appConfig.features.isDemoMode ? (
                  <span className="font-bold text-xl" style={{ color: APP_COLOR }}>
                    {APP_NAME}
                  </span>
                ) : (
                  <img
                    src={LOGO_SRC}
                    alt="Logo"
                    className="h-6 w-auto"
                  />
                )}
                <span className="bg-slate-100 text-xs px-2 py-1 rounded-md font-medium">
                  {role === "superadmin" ? "Superadmin" : "Admin"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Mobile close button */}
              <button
                onClick={() => setIsMobileOpen(false)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4">
            <ul className="space-y-2">
              {navItems.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      item.active ? "" : "text-gray-700 hover:bg-gray-100"
                    } ${collapsed ? "justify-center" : ""}`}
                    style={{
                      color: item.active ? APP_COLOR : "#374151",
                      backgroundColor: item.active
                        ? `${APP_COLOR}20`
                        : "transparent",
                    }}
                    title={collapsed ? item.label : undefined}
                  >
                    {item.icon}
                    {!collapsed && <span>{item.label}</span>}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>

      {/* Mobile content padding to prevent overlap */}
      <div className="lg:hidden h-16"></div>

      {/* Profile Image Modal */}
      <ProfileImageModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        currentUser={currentUser}
        onProfileImageUpdate={handleProfileImageUpdate}
        onUserInfoUpdate={handleUserInfoUpdate}
      />
    </>
  );
}
